import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, ExternalLink, TrendingUp, CheckCircle2 } from "lucide-react";
import { portfolioCompanies } from "@/data/portfolioCompanies";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import Navigation from "@/components/Navigation";
import DocumentViewer from "@/components/DocumentViewer";
import { useEffect } from "react";

const PortfolioCompany = () => {
  const { id } = useParams<{ id: string }>();
  const company = portfolioCompanies.find((c) => c.id === id);

  if (!company) {
    return <Navigate to="/portfolio" replace />;
  }

  const currentIndex = portfolioCompanies.findIndex((c) => c.id === id);
  const nextCompany = portfolioCompanies[(currentIndex + 1) % portfolioCompanies.length];

  useEffect(() => {
    document.title = `${company.name} - Portfolio Company | AMX Ventures`;
    const desc = `${company.name}: ${company.tagline || company.description} - AMX Ventures portfolio company scaling through hyperscaler partnerships and ecosystem-led growth.`;
    let meta = document.querySelector('meta[name="description"]') as HTMLMetaElement | null;
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'description');
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', desc.slice(0, 155));

    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', window.location.href);
  }, [company]);

  return (
    <div className="min-h-screen bg-background">
      <Navigation />

      <main>
        <header className="bg-secondary border-b-2 border-border">
          <div className="container mx-auto px-4 py-16 grid md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-8">
              <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-foreground">
                {company.name}
              </h1>
              {company.tagline && (
                <p className="mt-4 text-lg text-muted-foreground">{company.tagline}</p>
              )}
              <div className="mt-6 flex flex-wrap gap-3">
                {company.stage && <Badge variant="default">{company.stage}</Badge>}
                {company.category && <Badge variant="secondary">{company.category}</Badge>}
                {company.industry && <Badge variant="outline">{company.industry}</Badge>}
              </div>
              <div className="mt-8 flex flex-wrap gap-4">
                {company.website && (
                  <Button asChild>
                    <a
                      href={company.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`Visit ${company.name} website`}
                    >
                      Visit Website <ExternalLink className="ml-2 h-4 w-4" />
                    </a>
                  </Button>
                )}
                <Button asChild variant="outline">
                  <Link to="/portfolio">Back to Portfolio</Link>
                </Button>
                <Button asChild variant="ghost">
                  <Link to={`/portfolio/${nextCompany.id}`}>
                    Next Company <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </div>
            <div className="md:col-span-4 justify-self-end">
              <img
                src={company.logo}
                alt={`${company.name} logo`}
                className="h-16 w-auto md:h-20"
                loading="lazy"
              />
            </div>
          </div>
        </header>

        <section className="container mx-auto px-4 py-12 grid md:grid-cols-12 gap-8">
          <article className="md:col-span-8">
            <Card>
              <CardHeader>
                <CardTitle>About {company.name}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  {company.fullDescription || company.description}
                </p>
              </CardContent>
            </Card>

            {company.highlights && company.highlights.length > 0 && (
              <div className="mt-8 grid sm:grid-cols-2 gap-4">
                {company.highlights.map((h, idx) => (
                  <Card key={idx}>
                    <CardContent className="p-6 flex items-start gap-3">
                      <CheckCircle2 className="h-5 w-5 text-accent mt-1" />
                      <p className="text-foreground">{h}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </article>

          {company.metrics && (
            <aside className="md:col-span-4">
              <div className="grid gap-4">
                {Object.entries(company.metrics).map(([label, value]) => (
                  <Card key={label}>
                    <CardContent className="p-6">
                      <div className="flex items-center gap-2">
                        <TrendingUp className="h-4 w-4 text-accent" />
                        <span className="text-sm text-muted-foreground">{label}</span>
                      </div>
                      <div className="mt-2 text-2xl font-semibold">{value as string}</div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </aside>
          )}
        </section>

        {company.documents && company.documents.length > 0 && (
          <DocumentViewer documents={company.documents} companyName={company.name} />
        )}

        {/* Structured data for SEO */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Organization",
              name: company.name,
              url: company.website,
              description: company.tagline || company.description,
            }),
          }}
        />
      </main>
    </div>
  );
};

export default PortfolioCompany;
